<template>
  <div v-loading="loading">
    <div class="no-print mb-20">
      <el-button type="primary" @click="handlePrint">打印</el-button>
      <el-dropdown @command="handleExport" class="ml-10">
        <el-button type="success">
          导出<el-icon class="el-icon--right"><arrow-down /></el-icon>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="pdf">导出 PDF</el-dropdown-item>
            <el-dropdown-item command="xlsx">导出 Excel</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
    <div id="print-area" class="print-container">
      <component :is="registerComponent" :detail="printDetail" id="print" v-if="printDetail" ref="printComponent"/>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, computed, onBeforeMount, reactive, toRefs, ref, nextTick } from 'vue'
import { getDetailById } from '@/apis/Dictionary'
import { printTemplateList } from '@/utils/print'
import { cloneDeep } from 'lodash'
import { ArrowDown } from '@element-plus/icons-vue'
import { exportToPdf, exportQuotationToExcel } from '@/utils/export'

export default defineComponent({
  components: {
    ArrowDown
  },
  props: {
    id: { type: [Number, String], required: false, default: 0 },
    templateId: { type: [Number, String], required: false, default: 1 },
    detail: { type: Object, required: false, default: () => null }
  },
  setup(props) {
    let componentList: any = {}
    const printComponent = ref(null)
    const dataMap = reactive({
      printDetail: null,
      loading: false,
      templateId: props.templateId,
      async handlePrint() {
        const originalTitle = document.title
        document.title = `报价单_${dataMap.printDetail.no}`
        await nextTick()
        if (printComponent.value && printComponent.value.$refs.xTable) {
          const xTable = printComponent.value.$refs.xTable as any
          await xTable.recalculate()
        }
        window.print()
        setTimeout(() => {
          document.title = originalTitle
        }, 1000)
      },
      async handleExport(command) {
        const filename = `报价单_${dataMap.printDetail.no}`
        if (command === 'pdf') {
          dataMap.loading = true
          await exportToPdf('print-area', filename)
          dataMap.loading = false
        } else if (command === 'xlsx') {
          dataMap.loading = true
          await exportQuotationToExcel(dataMap.printDetail, filename, dataMap.templateId as any)
          dataMap.loading = false
        }
      }
    })
    const registerComponent = computed(() => {
      let returnCom = ''
      switch (dataMap.templateId) {
        case 1:
          returnCom = 'salesContract'
          break
        case 2:
          returnCom = 'singleWithoutBoth'
          break
        case 3:
          returnCom = 'singleWithoutParts'
          break
        case 4:
          returnCom = 'singleBusiness'
          break
        case 5:
          returnCom = 'multipleWidthoutReport'
          break
      }
      return componentList[returnCom]
    })
    const loadData = async() => {
      dataMap.loading = true
      let result = null
      let tempDetail = null
      if (props.id) {
        result = await getDetailById('quotation', props.id as any)
        result.details = result.details
          ? result.details.map((k, index) => {
            return {
              ...k,
              id: k.businessId,
              businessAmount: k.amount,
              no: `NO.${index + 1}`
            }
          })
          : []
      }
      tempDetail = props.id ? result : cloneDeep(props.detail)
      tempDetail = tempDetail || {}
      const templateObj = props.id ? printTemplateList.find(k => k.id === Number(tempDetail.templateName)) : printTemplateList.find(k => k.id === Number(dataMap.templateId))

      tempDetail.templateName = templateObj.name
      dataMap.templateId = props.id ? templateObj.id : props.templateId

      tempDetail.titleDetail = tempDetail.titleId ? await getDetailById('ticketTitle', tempDetail.titleId) : {}
      tempDetail.customerCompanyDetail = tempDetail.customerCompanyId ? await getDetailById('customerCompany', tempDetail.customerCompanyId) : {}
      tempDetail.customerCompanyContactDetail = tempDetail.customerCompanyContactId ? await getDetailById('customerCompanyContact', tempDetail.customerCompanyContactId) : {}
      tempDetail.salesManDetail = tempDetail.salesManId ? await getDetailById('user', tempDetail.salesManId) : {}
      tempDetail.customerTicketTitleDetail = tempDetail.customerCompanyTicketId ? await getDetailById('customerCompanyTicketTitle', tempDetail.customerCompanyTicketId) : {}
      tempDetail.quotationMarkTemplateDetail = tempDetail.quotationMarkTemplateId ? await getDetailById('quotationMarkTemplate', tempDetail.quotationMarkTemplateId) : {}

      dataMap.printDetail = tempDetail
      dataMap.loading = false
      // 提前设置 title，确保浏览器打印对话框能抓取到正确的文件名
      document.title = `报价单_${dataMap.printDetail.no}`

      nextTick(() => {
        if (printComponent.value && printComponent.value.$refs.xTable) {
          const xTable = printComponent.value.$refs.xTable as any
          xTable.recalculate()
        }
      })
    }
    const loadComponents = () => {
      const modulesFiles = require.context('./components', false, /\.vue$/)
      const resComponents: any = {}
      modulesFiles.keys().forEach(modulePath => {
        const moduleName = modulePath.replace(/^\.\/(.*)\.\w+$/, '$1')
        const value = modulesFiles(modulePath)
        resComponents[moduleName] = value.default
      })
      componentList = resComponents
    }
    onBeforeMount(() => {
      loadComponents()
      loadData()
    })
    return {
      registerComponent,
      printComponent,
      ...toRefs(dataMap)
    }
  }
})
</script>
<style lang="scss" scope>
.print-container {
  width: 210mm;
  margin: 0 auto;
  background: white;
  padding: 10mm;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
  min-height: 297mm;
  box-sizing: border-box;
  overflow: hidden; // 预览状态下如果内容过多也不要撑破容器
}

.no-print {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

@media print {
  @page {
    size: A4;
    margin: 10mm;
  }

  html, body {
    width: auto !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }

  /* 隐藏主应用 */
  #app {
    display: none !important;
  }

  /* 将 dialog 遮罩层及内容恢复为标准文档流 */
  .el-overlay, .el-overlay-dialog {
    position: static !important;
    inset: auto !important;
    background: none !important;
    overflow: visible !important;
    height: auto !important;
    min-height: 0 !important;
  }

  .el-dialog {
    position: static !important;
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: none !important;
    min-height: 0 !important;
    box-shadow: none !important;
    background: none !important;
    border: none !important;
    transform: none !important;
  }

  .el-dialog__header, .el-dialog__footer {
    display: none !important;
  }

  .el-dialog__body {
    padding: 0 !important;
    overflow: visible !important;
    min-height: 0 !important;
  }

  /* 页边距交给 @page 处理，避免 A4 可打印区域外溢生成空白页 */
  .print-container {
    display: block !important;
    position: static !important;
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    border: none !important;
    background: white !important;
    z-index: 999999 !important;
    overflow: visible !important;
    box-sizing: border-box !important;
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  .print-template {
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  /* 强制 vxe-table 内部容器完全展开 */
  .vxe-table--render-wrapper,
  .vxe-table--main-wrapper,
  .vxe-table--body-wrapper,
  .vxe-table--header-wrapper,
  .vxe-table--footer-wrapper,
  .vxe-table--inner-wrapper,
  .vxe-table--viewport-wrapper,
  .vxe-table--layout-wrapper {
    overflow: visible !important;
    width: 100% !important;
    max-width: 100% !important;
    transform: none !important;
  }

  .vxe-table--header,
  .vxe-table--body,
  .vxe-table--footer,
  .vxe-table {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* 移除缩放和强制边距，以便正确分页 */
  .vxe-table {
    transform: none !important;
    zoom: 1 !important;
    margin-bottom: 0 !important;
    page-break-inside: auto !important;
  }

  .vxe-table--horizontal-scrollbar,
  .vxe-table--vertical-scrollbar,
  .vxe-table--scroll-x-virtual,
  .vxe-table--scroll-y-virtual,
  ::-webkit-scrollbar {
    display: none !important;
  }

  .vxe-body--column, .vxe-header--column, .vxe-footer--column {
    padding: 2px 0 !important;
    font-size: 10px !important;
  }

  .no-print {
    display: none !important;
  }
}
</style>
